"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CompiledFormulaExpression = void 0;
const FormulaInterpreter_1 = require("../interpreter/FormulaInterpreter");
const FormulaParser_1 = require("../parser/FormulaParser");
const FormulaTokenizer_1 = require("../tokenizer/FormulaTokenizer");
/**
 * Implementation of the CompiledExpression interface for formula expressions.
 *
 * This class provides a concrete implementation that compiles a formula expression
 * once and allows efficient repeated evaluation with different data sets.
 *
 * @class
 * @implements {CompiledExpression}
 */
class CompiledFormulaExpression {
    /**
     * Creates a new instance of CompiledFormulaExpression.
     *
     * @param {string} expression - The formula expression to compile.
     *        Must be a valid expression that can be parsed by the formula engine.
     *
     * @throws {FormulaInterpreterError} When expression syntax is invalid during compilation
     * @throws {IncorrectSyntax} When expression has incorrect syntax during compilation
     */
    constructor(expression) {
        this.expression = expression;
        /** Type identifier for the compiled expression */
        this.type = "CompiledExpression";
        this._ast = new FormulaParser_1.FormulaParser().execute(new FormulaTokenizer_1.FormulaTokenizer().execute(expression));
    }
    /**
     * Evaluates the compiled expression with the provided data.
     *
     * @template T - The type of the data object containing variables
     * @param {T} data - The data object to evaluate the expression against.
     *        Should contain all variables referenced in the expression.
     * @returns {string | number} The result of the evaluation
     *
     * @example
     * ```typescript
     * const expr = new CompiledFormulaExpression("price * quantity");
     * expr.evaluate({ price: 10, quantity: 5 }); // 50
     * ```
     *
     * @throws {FormulaVariableNotFoundError} When required variables are missing from data
     */
    evaluate(data) {
        return new FormulaInterpreter_1.FormulaInterpreter().execute(this._ast, data);
    }
    /**
     * Returns the string representation of the original expression.
     *
     * @returns {string} The original expression string that was compiled
     */
    toString() {
        return this.expression;
    }
}
exports.CompiledFormulaExpression = CompiledFormulaExpression;
